import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../admin/auth/services/auth.service';

export interface RouteCard {
  id: number;
  title: string;
  description: string;
  icon: string;
  route: string;
  category: string;
  accentColor: string;
}

export interface RouteCategory {
  id: number;
  name: string;
  icon: string;
  accentColor: string;
  cards: RouteCard[];
}

const ROUTE_DESCRIPTIONS: Record<string, string> = {
  'admin/roles': 'Gestión de roles y asignación de permisos',
  'admin/users': 'Administración de usuarios y cuentas de acceso',
  'admin/ruta': 'Gestión del catálogo de rutas del sistema',
  'admin/categoriaRutas': 'Administración de categorías de módulos',
  'admin/audit': 'Registro de auditoría y actividad del sistema',
  'admin/one-piece-collection': 'Catálogo y seguimiento de cartas One Piece TCG',
  'admin/op-collection': 'Catálogo y seguimiento de cartas One Piece TCG',
  'nino-shopping/clientes': 'Directorio y gestión de clientes',
  'nino-shopping/ventas': 'Registro y seguimiento de ventas realizadas',
  'nino-shopping/productos': 'Inventario y catálogo de productos en tienda',
  'nino-shopping/stats': 'Métricas de rendimiento y estadísticas generales',
  'nino-shopping/inversion': 'Control de inversiones y compras de mercadería',
  'nino-shopping/perdidas': 'Registro y análisis de pérdidas operativas',
  'nino-shopping/registrarCompra': 'Registro ágil de nuevas adquisiciones',
  'finanzas/finanzas': 'Gestión financiera, ingresos y balances',
  'admin/gastos': 'Gestión y carga masiva de gastos',
  'admin/scraper/mangas-generales': 'Rastreo y monitoreo de novedades de manga general',
  'admin/scraper/manga-one-piece': 'Seguimiento de lanzamientos de manga One Piece',
  'admin/scraper/one-piece-oficial': 'Monitoreo de anuncios y novedades en web oficial',
  'admin/scraper/one-piece-tiendas': 'Rastreo de stock y precios de One Piece TCG en tiendas',
  'admin/scraper/productos-especificos': 'Monitoreo de artículos y figuras específicas',
  'admin/telegram': 'Configuración de bots y alertas automáticas por Telegram',
  'admin/telegram-groups': 'Administración de canales y grupos receptores de alertas',
  'admin/one-piece-products': 'Monitoreo y scraping de productos One Piece',
  'admin/torneos': 'Monitoreo en vivo de torneos TCG y alertas de cupos'
};

const CATEGORY_STYLES: Record<string, { accentColor: string; fallbackIcon: string }> = {
  'administrador': { accentColor: '#e11d48', fallbackIcon: 'fa-user-secret' },
  'admin': { accentColor: '#e11d48', fallbackIcon: 'fa-shield-halved' },
  'tienda': { accentColor: '#10b981', fallbackIcon: 'fa-cart-shopping' },
  'finanzas': { accentColor: '#06b6d4', fallbackIcon: 'fa-wallet' },
  'scraper': { accentColor: '#f59e0b', fallbackIcon: 'fa-spider' },
  'coleccionables': { accentColor: '#f43f5e', fallbackIcon: 'fa-layer-group' }
};

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  isLoading = true;
  userName = '';
  userRole = '';
  allCategories: RouteCategory[] = [];
  filteredCategories: RouteCategory[] = [];
  searchQuery = '';
  totalRoutesCount = 0;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadUserData();
    this.loadAssignedRoutes();
  }

  private loadUserData(): void {
    const user = this.authService.getUserData();
    if (user) {
      this.userName = user.nombre || user.username || '';
      this.userRole = user.rol || (user.rolId === 1 ? 'Administrador' : 'Usuario');
    }
  }

  private loadAssignedRoutes(): void {
    this.isLoading = true;
    const user = this.authService.getUserData();
    const rolId = user?.rolId ?? user?.idRol ?? this.authService.getUserRoleId();

    if (!rolId) {
      console.warn('⚠️ No se encontró rolId para el usuario actual');
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.authService.getPermissions(rolId).pipe(
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (permisos: any[]) => {
        try {
          if (Array.isArray(permisos) && permisos.length > 0) {
            // Sincronizar rutaMap para permissionGuard de forma segura
            try {
              this.authService.setRutaMapFromCategorias(permisos);
            } catch (storageErr) {
              console.warn('⚠️ No se pudo guardar rutaMap en storage:', storageErr);
            }

            this.allCategories = this.mapPermissionsToCategories(permisos);
            this.totalRoutesCount = this.allCategories.reduce((acc, cat) => acc + cat.cards.length, 0);
            this.filterCategories();
          } else {
            this.allCategories = [];
            this.filteredCategories = [];
            this.totalRoutesCount = 0;
          }
        } catch (err) {
          console.error('❌ Error al procesar categorías:', err);
          this.allCategories = [];
          this.filteredCategories = [];
        }

        this.isLoading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('❌ Error al cargar rutas asignadas al usuario:', err);
        this.isLoading = false;
        this.allCategories = [];
        this.filteredCategories = [];
        this.totalRoutesCount = 0;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    });
  }

  private mapPermissionsToCategories(rawCategories: any[]): RouteCategory[] {
    if (!Array.isArray(rawCategories)) return [];

    return rawCategories
      .map(cat => {
        if (!cat) return null;
        const catNameKey = (cat.nombre || '').trim().toLowerCase();
        const style = CATEGORY_STYLES[catNameKey] || { accentColor: '#8b5cf6', fallbackIcon: 'fa-folder' };
        const catIcon = this.cleanIcon(cat.icono, style.fallbackIcon);

        const cards: RouteCard[] = (cat.rutas || [])
          .filter((r: any) => r && r.ruta)
          .map((r: any) => {
            const cleanPath = (r.ruta || '').trim().replace(/^\/+/, '');
            const cardIcon = this.cleanIcon(r.icon, 'fa-circle');
            const description = ROUTE_DESCRIPTIONS[cleanPath] || `Acceder a ${r.nombre || 'módulo'}`;

            return {
              id: r.id || r.idRuta || 0,
              title: r.nombre || 'Módulo',
              description,
              icon: cardIcon,
              route: `/${cleanPath}`,
              category: cat.nombre || '',
              accentColor: style.accentColor
            };
          });

        return {
          id: cat.id || 0,
          name: cat.nombre || 'General',
          icon: catIcon,
          accentColor: style.accentColor,
          cards
        };
      })
      .filter((cat): cat is RouteCategory => !!cat && cat.cards && cat.cards.length > 0);
  }

  private cleanIcon(icon: string | undefined, fallback: string): string {
    if (!icon || typeof icon !== 'string') return fallback;
    const trimmed = icon.trim();
    if (trimmed.startsWith('fa-') || trimmed.startsWith('fa ')) {
      return trimmed;
    }
    // Si viene solo un id numérico
    if (/^\d+$/.test(trimmed)) return fallback;
    return `fa-${trimmed}`;
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input ? input.value : '';
    this.filterCategories();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filterCategories();
  }

  private filterCategories(): void {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) {
      this.filteredCategories = this.allCategories;
      return;
    }

    this.filteredCategories = this.allCategories
      .map(cat => ({
        ...cat,
        cards: cat.cards.filter(c =>
          c.title.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.route.toLowerCase().includes(q)
        )
      }))
      .filter(cat => cat.cards.length > 0);
  }

  navigate(route: string): void {
    if (!route) return;
    const cleanRoute = route.startsWith('/') ? route : `/${route}`;
    this.router.navigate([cleanRoute]);
  }
}
